import React, { useState, useRef } from "react";
import { footerData } from "../helpers/FooterData";
import { Link } from "react-router-dom";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { MdArrowForwardIos } from "react-icons/md";
import { useTranslation } from "react-i18next";
import axios from "axios";
import API_BASE_URL from "../config/api";

const navLinks = [
  { to: "/entreprise",        label: "L'entreprise" },
  { to: "/domaines",          label: "Domaines" },
  { to: "/activites",         label: "Activités" },
  { to: "/cibles-partenaires",label: "Cibles et Partenaires" },
  { to: "/publications",      label: "Publications" },
  { to: "/news-presse",       label: "News presse" },
  { to: "/evenement",         label: "Événements" },
  { to: "/nous-contacter",    label: "Nous contacter" },
];

const Footer = () => {
  const { t } = useTranslation();
  const toast = useRef(null);

  const [newsLetterEmail, setEmail] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);

  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsValidEmail(validateEmail(e.target.value));
  };

  const subscribeNewsletter = (e) => {
    e.preventDefault();
    if (!isValidEmail) {
      alert("Adresse e-mail invalide. Veuillez saisir une adresse e-mail valide.");
      return;
    }
    axios
      .post(`${API_BASE_URL}/sendmail/newsLetter`, { newsLetterEmail })
      .then((res) => {
        if (res.data.error) {
          toast.current.show({
            severity: "error",
            summary: "Erreur d'abonnement",
            detail: "Cette adresse e-mail est déjà inscrite à la newsletter.",
          });
        } else {
          toast.current.show({
            severity: "success",
            summary: "Abonnement réussi",
            detail: "Nous vous recontacterons bientôt !",
          });
          setEmail("");
        }
      })
      .catch(() => alert("Vous êtes déjà abonné(e) à la newsletter de GS7S."));
  };

  return (
    <footer className="bg-gray-950 text-white">
      <Toast ref={toast} />

      {/* ── Main grid ── */}
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">

        {/* Coordonnées */}
        <div>
          <h3 className="text-base font-bold mb-5 pb-2 border-b-2 border-red-600 w-fit tracking-wide uppercase">
            {t("Coordonnées")}
          </h3>
          <ul className="flex flex-col gap-4">
            {footerData.slice(0, 3).map((icon) => (
              <li key={icon.id} className="flex items-start gap-3">
                <span className="text-red-500 mt-0.5 shrink-0">{icon.footerIcon}</span>
                <span className="text-gray-400 text-sm leading-relaxed">{icon.footerDesc}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Navigation */}
        <div>
          <h3 className="text-base font-bold mb-5 pb-2 border-b-2 border-red-600 w-fit tracking-wide uppercase">
            Navigation
          </h3>
          <ul className="grid grid-cols-1 gap-2">
            {navLinks.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="flex items-center gap-2 text-gray-400 text-sm hover:text-red-500 transition-colors duration-200 no-underline group"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0 group-hover:scale-125 transition-transform duration-200" />
                  {t(item.label)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Newsletter + réseaux */}
        <div className="sm:col-span-2 lg:col-span-1">
          <h3 className="text-base font-bold mb-5 pb-2 border-b-2 border-red-600 w-fit tracking-wide uppercase">
            Newsletter
          </h3>
          <p className="text-gray-400 text-sm mb-4 leading-relaxed">
            Abonnez-vous pour recevoir nos dernières actualités et publications.
          </p>

          <form onSubmit={subscribeNewsletter} className="flex flex-col gap-3">
            <InputText
              id="newsletter-email"
              placeholder="Votre adresse e-mail"
              className={`w-full bg-gray-800 text-white text-sm rounded-lg px-4 py-2.5 placeholder-gray-500 outline-none transition-colors duration-200 ${
                isValidEmail
                  ? "border border-gray-700 focus:border-red-500"
                  : "border border-red-500"
              }`}
              value={newsLetterEmail}
              onChange={handleEmailChange}
              required
            />
            <button
              type="submit"
              className="bg-red-600 hover:bg-red-700 active:scale-95 text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 w-full sm:w-fit"
            >
              {t("S'abonner")} <MdArrowForwardIos size={13} />
            </button>
          </form>

          {/* Réseaux sociaux */}
          <div className="flex gap-4 mt-7">
            {footerData.slice(3, 7).map((icon) => (
              <a
                key={icon.id}
                href={icon.footerDesc || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-red-500 hover:-translate-y-1 transition-all duration-200"
              >
                {icon.footerIcon}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-1">
          <p className="text-gray-500 text-xs text-center sm:text-left">
            © 2015-{new Date().getFullYear()} <span className="text-gray-300 font-medium">GS7S</span> — Tous droits réservés
          </p>
          <p className="text-gray-600 text-xs">Dakar, Sénégal</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
